// CinematicFelt.tsx (CX2) - the app's oval felt, REBOUND to the CX1 frame stream so a hand plays out action
// by action. This is the "rebind OvalFelt to the frame stream" deliverable of CX2: it renders the SAME skin
// as <OvalFelt> (gold oval, monochrome hero dock, villain pods, .pcard cards - felt.css) and ADDS the POC's
// proven cinematic layers on top: chips out front accumulating per seat, the sweep to center at street close,
// the pot count-up, the award/push to the winner, board-deal timing (flop 3-at-once, turn/river alone), turn
// highlight, fold dimming, the self-paced stepper, and the two-tier commentary (bubble + running ledger).
//
// The static <OvalFelt view={FeltView}> stays exactly as-is for the authored teaching felts; CX4 binds THIS
// component into the live Observe/Diagnose/Solo modes. Build to docs/ui-explorations/CX0_CINEMATIC_TABLE_SPEC.md.
//
// POC's hard-won fixes carried forward (CX0 §2.4), do NOT rediscover:
//   - NO AnimatePresence exit-driven unmount (it stranded chips + wedged the pane). A changing `key` remounts
//     + animates IN; no exit tracking.
//   - NO 3D rotateY deal (it collapsed card width to 0). The board cards animate translateY + scale only.
//   - Static-CSS anchor owns position; motion owns `transform` (x/y/scale) ONLY. If both fight over transform
//     the chip jumps half its own size off the spot. Every front/sweep chip sits in a positioned .frontAnchor
//     and the motion.div inside it animates transform.
//   - The sweep travel is measured in PIXELS (ResizeObserver): motion's x/y are element-relative, so the
//     to-center delta is computed from the measured felt box, not percentages.
import { animate, motion, useReducedMotion } from 'motion/react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { ArchetypeId, CardIndex } from '@postflop/schema';
import { Card } from './Card';
import { ChipStack } from './Chip';
import { ArchetypeTerm } from './ArchetypeTerm';
import { money, ringFor } from '../engine/bridge';
import { displayName } from '../engine/liveRunner';
import { useSequence } from './useSequence';
import type { AwardFrame, HandFrame, HandStream } from '../engine/frameStream';

/** One narration beat over the felt. CX2 renders the two-tier commentary UI; CX3 supplies the real
 *  selection + prose. The demo harness hand-authors these to exercise the bubble + ledger. */
export interface Beat {
  text: string;
  /** hero's own decision: gets the bigger word budget + visual emphasis (CX0 §3.2). */
  decision?: boolean;
  /** CX9 - the villain archetype this beat reads (a villain moment, or the villain a hero beat names). When
   *  the beat text contains that type's display name, the bubble renders the name as a tap-to-learn term.
   *  Undefined (the opening / the award) means no name to make tappable, so the text renders plain. */
  archetypeId?: ArchetypeId;
}

/** Split a beat's text so the villain's display name ("The Surgeon") becomes a tap-to-learn <ArchetypeTerm>
 *  in place, and the rest stays prose (CX9). No archetype, or the name not present in the text, renders the
 *  text unchanged. Every occurrence is wrapped, in case the pro names the type twice. */
function beatNodes(text: string, id: ArchetypeId | undefined): ReactNode {
  if (!id) return text;
  const name = displayName(id);
  if (!name || !text.includes(name)) return text;
  const parts = text.split(name);
  return parts.map((part, i) => (
    <span key={i}>
      {part}
      {i < parts.length - 1 && (
        <ArchetypeTerm id={id}>{name}</ArchetypeTerm>
      )}
    </span>
  ));
}

export interface CinematicFeltProps {
  stream: HandStream;
  /** per-FRAME beats keyed by frame ordinal `i`. A missing key = the pro says nothing (silence is
   *  structural, CX0 §3.1). Optional: with none, the felt still plays; the bubble reads "(watching)". */
  beats?: Record<number, Beat>;
  /**
   * CX4 - the do-mode HALT boundary: the felt auto-plays the action up to this frame (hero's next decision)
   * and stops there for hero to act. Undefined = the Observe watch (play the whole hand, self-paced).
   */
  haltAt?: number;
  /** CX4 - start the playback running (the do-modes bring the action to hero). Default false = self-paced. */
  autoplay?: boolean;
  /** CX4 - show the watch scrub controls + running ledger + progress rail (Observe). The do-modes pass
   *  false: hero advances the hand by DECIDING, not by scrubbing, so only the felt + the pro's bubble show. */
  controls?: boolean;
  /** CX4 - report the current frame ordinal to the host, so a do-mode wrapper knows when the felt has
   *  ARRIVED at hero's decision (to arm the action ramp) versus is still bringing the action to him. */
  onIndex?: (i: number) => void;
  /** CX-POLISH - the frame to mount at (default 0). A host that unmounts the felt for an excursion and comes
   *  back (Diagnose) passes the last reported index so the remount resumes there instead of re-running the
   *  hand from frame 0. See useSequence's `startAt`. */
  startAt?: number;
}

/** left/top as numbers (percent of the felt box). */
interface Pt {
  left: number;
  top: number;
}

const ACT_WORD: Record<string, string> = {
  fold: 'Fold',
  check: 'Check',
  call: 'Call',
  bet: 'Bet',
  raise: 'Raise',
  jam: 'All in',
};

const wordsIn = (s: string): number => s.trim().split(/\s+/).length;

/** The felt's true center - the board sits here, the pot directly below (CX0 §4.2: board pulled to true
 *  center ~47%). Chips out front and the sweep target this. */
const CENTER: Pt = { left: 50, top: 47 };
/** Hero's anchor for out-front chips: the dock is CSS-positioned at the bottom; this is only where hero's
 *  bet chips rest, between the dock and the center. */
const HERO_ANCHOR: Pt = { left: 50, top: 90 };

/** A bet's chips live between the player and the middle, as their own object (research/08a). 0.58 of the way
 *  from center to the pod keeps them clear of both the pot and the pod (CX0 §4.4 risk 2). */
const chipAnchor = (pod: Pt): Pt => ({
  left: CENTER.left + (pod.left - CENTER.left) * 0.58,
  top: CENTER.top + (pod.top - CENTER.top) * 0.58,
});

/** Parse a ring slot's "24%" -> 24. */
const pct = (s: string): number => parseFloat(s);

export function CinematicFelt({ stream, beats, haltAt, autoplay, controls = true, onIndex, startAt }: CinematicFeltProps) {
  const frames = stream.frames;
  const N = frames.length;
  const reduce = useReducedMotion();

  // ── the beat driver (self-paced by default; autoplay opt-in) ───────────────────────────────────────────
  const delayFor = (i: number): number => {
    const f = frames[i];
    if (!f) return 1500;
    if (f.phase === 'award') return 4000;
    const base = !f.action ? 1500 : f.action.kind === 'fold' ? 620 : f.action.kind === 'check' ? 780 : f.action.kind === 'call' ? 1050 : 1300;
    const read = beats?.[i] ? wordsIn(beats[i].text) * 333 : 0;
    return base + (f.sweep ? 850 : 0) + read;
  };
  const seq = useSequence(N, delayFor, { haltAt, autoplay, startAt });

  // report the current frame up to the host (CX4): a do-mode wrapper arms hero's ramp only once the felt has
  // brought the action to him (seq.i has reached the halt), not while villains are still acting into it.
  useEffect(() => {
    onIndex?.(seq.i);
  }, [seq.i, onIndex]);

  const f: HandFrame = frames[Math.min(seq.i, N - 1)];
  const prev: HandFrame | undefined = seq.i > 0 ? frames[seq.i - 1] : undefined;
  const isAward = f.phase === 'award';
  const award = isAward ? (f as AwardFrame).award : null;
  const beat = beats?.[seq.i];

  // ── geometry: engine seat -> ring slot (stable across frames, keyed off the stream's seat order) ────────
  const heroSeat = stream.heroSeat;
  const podPos = useMemo(() => {
    const villainSeats = stream.seats.map((s) => s.seat).filter((s) => s !== heroSeat);
    const ring = ringFor(villainSeats.length);
    const map = new Map<number, Pt>();
    villainSeats.forEach((seat, idx) => {
      const slot = ring[idx]?.layout;
      if (slot) map.set(seat, { left: pct(slot.left), top: pct(slot.top) });
    });
    return map;
  }, [stream.seats, heroSeat]);

  /** the on-felt anchor for a seat (villain ring slot, or hero's bottom anchor). */
  const anchorFor = (seat: number): Pt => (seat === heroSeat ? HERO_ANCHOR : podPos.get(seat) ?? CENTER);
  const labelOf = (seat: number): string => stream.seats.find((s) => s.seat === seat)?.label ?? String(seat);

  // ── arrow keys drive the hand (right/down/space = next, left/up = back) ────────────────────────────────
  useLayoutEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault();
        seq.next();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        seq.prev();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [seq]);

  // ── measure the felt in pixels so the sweep travels a real distance (motion x/y are element-relative) ──
  const feltRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = feltRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setBox({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    setBox({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);
  /** pixel delta from a seat's chip spot to the center, for the sweep + the award push. */
  const toCenter = (seat: number) => {
    const a = chipAnchor(anchorFor(seat));
    return { x: ((CENTER.left - a.left) / 100) * box.w, y: ((CENTER.top - a.top) / 100) * box.h };
  };

  // board-deal cadence: cards new THIS frame (index >= prev board length) animate in; the flop's three
  // stagger 130ms apart, the turn/river land alone (CX0 §2.1).
  const prevBoardLen = prev?.board.length ?? 0;

  // running ledger: one row per NARRATED frame (a beat with text), CX0 §4.2. Clickable -> jump to that beat.
  const ledger = useMemo(
    () =>
      frames
        .map((fr, i) => ({ i, fr, beat: beats?.[i] }))
        .filter((r): r is { i: number; fr: HandFrame; beat: Beat } => Boolean(r.beat)),
    [frames, beats],
  );

  const dur = (ms: number) => (reduce ? ms / 2 : ms);
  const shownBySeat = new Map((award?.shown ?? []).map((s) => [s.seat, s.cards]));

  return (
    <div className="tablewrap">
      <div className="felt cine" ref={feltRef}>
        {/* ── center: board + pot ─────────────────────────────────────────────────────────────────────── */}
        <div className="center">
          <div className="board">
            {f.board.map((c, k) => (
              // keyed by the card itself: a card already down stays put; only a genuinely new card animates in.
              <motion.div
                key={c}
                className="bcard"
                initial={{ opacity: 0, y: reduce ? 0 : -22, scale: reduce ? 1 : 0.82 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: dur(340) / 1000, delay: (Math.max(0, k - prevBoardLen) * dur(130)) / 1000, ease: 'easeOut' }}
              >
                <Card card={c as CardIndex} />
              </motion.div>
            ))}
          </div>
          {/* the pot always stays visible (CX0 §4.2). It counts up on the sweep; on the award frame it is $0. */}
          <div className="potpill">
            <span className="lab">Pot</span> <PotValue value={f.settledPot} reduce={!!reduce} />
          </div>
        </div>

        {/* ── chips out front (this frame's live fronts) ──────────────────────────────────────────────── */}
        {!isAward &&
          f.seats
            .filter((s) => s.front > 0)
            .map((s) => {
              const a = chipAnchor(anchorFor(s.seat));
              return (
                <div key={`front-${s.seat}`} className="frontAnchor" style={{ left: `${a.left}%`, top: `${a.top}%` }}>
                  <motion.div
                    className="front"
                    key={s.front}
                    initial={{ opacity: 0, scale: reduce ? 1 : 0.65 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: dur(220) / 1000, ease: 'easeOut' }}
                  >
                    <ChipStack amount={s.front} />
                    {money(s.front)}
                  </motion.div>
                </div>
              );
            })}

        {/* ── THE SWEEP: on a street-close frame, the PREVIOUS frame's fronts fly to center as the pot jumps.
             Keyed to the beat index so it mounts fresh and plays exactly once. ──────────────────────────── */}
        {f.sweep &&
          prev &&
          prev.seats
            .filter((s) => s.front > 0)
            .map((s) => {
              const a = chipAnchor(anchorFor(s.seat));
              const d = toCenter(s.seat);
              return (
                <div key={`sweep-${seq.i}-${s.seat}`} className="frontAnchor" style={{ left: `${a.left}%`, top: `${a.top}%` }}>
                  <motion.div
                    className="front"
                    initial={{ opacity: 1, x: 0, y: 0, scale: 1 }}
                    animate={{ opacity: 0, x: reduce ? 0 : d.x, y: reduce ? 0 : d.y, scale: 0.4 }}
                    transition={{ duration: dur(500) / 1000, ease: 'easeIn' }}
                  >
                    <ChipStack amount={s.front} />
                    {money(s.front)}
                  </motion.div>
                </div>
              );
            })}

        {/* ── THE AWARD PUSH: on the award frame, the settled pot pushes from center to the winner(s). ──── */}
        {isAward &&
          award &&
          award.winners.map((seat) => {
            const d = toCenter(seat);
            return (
              <div key={`push-${seat}`} className="frontAnchor" style={{ left: `${CENTER.left}%`, top: `${CENTER.top}%` }}>
                <motion.div
                  className="front win"
                  initial={{ opacity: 0, x: 0, y: 0, scale: 0.6 }}
                  animate={{ opacity: [0, 1, 1, 0], x: reduce ? 0 : -d.x, y: reduce ? 0 : -d.y, scale: 1 }}
                  transition={{ duration: dur(900) / 1000, ease: 'easeOut' }}
                >
                  <ChipStack amount={award.amount} />
                  {money(award.amount)}
                </motion.div>
              </div>
            );
          })}

        {/* ── villain pods on the ring ────────────────────────────────────────────────────────────────── */}
        {[...podPos.entries()].map(([seat, pos]) => {
          const s = f.seats.find((x) => x.seat === seat);
          if (!s) return null;
          const acted = !isAward && f.action?.seat === seat ? f.action : null;
          const live = !isAward && !s.folded && f.toAct === seat;
          const label = labelOf(seat);
          const shown = shownBySeat.get(seat);
          const cls = 'seat' + (s.folded ? ' out' : '') + (live ? ' act' : '');
          return (
            <div key={seat} className={cls} style={{ left: `${pos.left}%`, top: `${pos.top}%` }}>
              {/* showdown: a revealed villain flips his hole cards up above the pod on the award frame. */}
              {shown && !s.folded && (
                <div className="showhole">
                  {shown.map((c) => (
                    <Card key={c} card={c} />
                  ))}
                </div>
              )}
              <div className="av">{label.charAt(0).toUpperCase()}</div>
              {seat === stream.buttonSeat && (
                <span className="btn-d" title="Dealer button">
                  D
                </span>
              )}
              <div className="nm">{label}</div>
              <motion.div className="st num" key={s.stack} initial={{ scale: award?.winners.includes(seat) ? 1.28 : 1 }} animate={{ scale: 1 }}>
                {money(s.stack)}
              </motion.div>
              {s.folded && <div className="foldtag">folded</div>}
              {acted && <div className={'actword' + (acted.kind === 'bet' || acted.kind === 'raise' || acted.kind === 'jam' ? ' aggro' : '')}>{ACT_WORD[acted.kind] ?? acted.kind}</div>}
            </div>
          );
        })}

        {/* ── hero dock (bottom-center, big + gold) ───────────────────────────────────────────────────── */}
        {(() => {
          const hs = f.seats.find((x) => x.seat === heroSeat);
          const acted = !isAward && f.action?.seat === heroSeat ? f.action : null;
          const live = !isAward && !!hs && !hs.folded && f.toAct === heroSeat;
          return (
            <div className={'hero' + (hs?.folded ? ' out' : '') + (live ? ' act' : '')}>
              <div className="cards">
                <Card card={stream.heroCards[0]} />
                <Card card={stream.heroCards[1]} />
              </div>
              <div className="lab">{labelOf(heroSeat)}</div>
              <div className="hrow">
                {stream.buttonSeat === heroSeat && <span className="btn-static">D</span>}
                {hs && <span className="hst num">{money(hs.stack)}</span>}
                {acted && <span className={'actword' + (acted.kind === 'bet' || acted.kind === 'raise' || acted.kind === 'jam' ? ' aggro' : '')}>{ACT_WORD[acted.kind] ?? acted.kind}</span>}
              </div>
            </div>
          );
        })()}
      </div>

      {/* ── (a) in-the-moment bubble: ONE self-replacing pane (chess.com's coach), never a stack of lines.
           No AnimatePresence - a changing key remounts + fades in, no exit to wedge on (CX0 §3.3, §2.4). ── */}
      <div className="cinenarr">
        <div className="who">P</div>
        <motion.div
          key={seq.i}
          className={beat ? 'txt' + (beat.decision ? ' decision' : '') : 'quiet'}
          initial={{ opacity: 0, y: reduce ? 0 : 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: dur(220) / 1000, ease: 'easeOut' }}
        >
          {beat ? beatNodes(beat.text, beat.archetypeId) : '(watching)'}
        </motion.div>
      </div>

      {/* ── (b) the watch scrub controls + rail + ledger. Hidden in the do-modes (controls=false): hero moves
           the hand forward by DECIDING, so scrubbing past his own turn makes no sense; the bubble stays. ── */}
      {controls && (
        <>
      <div className="cinectl">
        <button type="button" onClick={seq.toggle}>
          {seq.playing ? 'Pause' : 'Play'}
        </button>
        <button type="button" onClick={seq.prev} disabled={seq.i === 0}>
          Back
        </button>
        <button type="button" onClick={seq.next} disabled={seq.atEnd}>
          Next
        </button>
        <button type="button" onClick={seq.restart}>
          Restart
        </button>
        <span className="spacer" />
        <span className="beat">
          {seq.i + 1} / {N} · {isAward ? 'award' : f.street}
        </span>
      </div>
      <div className="cinerail">
        <div className="fill" style={{ width: `${((seq.i + 1) / N) * 100}%` }} />
      </div>

      {/* ── (c) the running ledger below the controls: read the hand back IN CONTEXT while the table stays in
           view above. One row per narrated beat, current highlighted, click to jump (CX0 §4.2). ─────────── */}
      {ledger.length > 0 && (
        <div className="ledger" aria-label="Hand commentary">
          {ledger.map((row) => (
            <button
              type="button"
              key={row.i}
              className={'lrow' + (row.i === seq.i ? ' now' : '') + (row.beat.decision ? ' decision' : '')}
              onClick={() => seq.goTo(row.i)}
            >
              <span className="lstreet">{row.fr.phase === 'award' ? 'award' : row.fr.street}</span>
              <span className="ltext">{row.beat.text}</span>
            </button>
          ))}
        </div>
      )}
        </>
      )}
    </div>
  );
}

/** The pot number, animated old->new via motion's `animate` + onUpdate (CX0 §4.2: count-up, no counter
 *  library). Handles arbitrary transitions (a Back step counts DOWN just as smoothly). A settle-timer
 *  guarantees the RESTING value is always the target even if the frame loop is throttled (e.g. a
 *  backgrounded tab where requestAnimationFrame - which motion's animate() is driven by - never ticks): the
 *  count-up is the enhancement, the correct total is not negotiable. */
function PotValue({ value, reduce }: { value: number; reduce: boolean }) {
  const [disp, setDisp] = useState(value);
  const prev = useRef(value);
  useEffect(() => {
    if (reduce || prev.current === value) {
      setDisp(value);
      prev.current = value;
      return;
    }
    const controls = animate(prev.current, value, {
      duration: 0.4,
      ease: 'easeOut',
      onUpdate: (v) => setDisp(Math.round(v)),
      onComplete: () => setDisp(value),
    });
    // rAF-independent backstop: setTimeout still fires when the frame loop is throttled, so the pot never
    // gets stranded at the animation's start value.
    const settle = setTimeout(() => setDisp(value), 450);
    prev.current = value;
    return () => {
      controls.stop();
      clearTimeout(settle);
    };
  }, [value, reduce]);
  return <span className="num">{money(disp)}</span>;
}
